import React, { useState, useEffect, useRef } from 'react';
import { Search, Check, Users, ChevronDown, Plus, Globe, Building2, Mail, Phone } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * CustomerPicker — Premium autocomplete picker for financial customers.
 * Parity with SupplierSelector / Procurement pickers.
 *
 * Props:
 *  - value: currently selected customer id (or null)
 *  - onSelect(customer): full customer object (with address object, trn, website, phone, email, company_name)
 *  - onCreateNew(): optional callback to open a "new customer" modal
 *  - testId: base data-testid prefix
 */
const CustomerPicker = ({ value, onSelect, onCreateNew, testId = 'customer-picker' }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const rootRef = useRef(null);
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchCustomers('');
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (value && customers.length) {
      const found = customers.find((c) => c.id === value);
      if (found) setSelected(found);
    }
    if (!value) setSelected(null);
  }, [value, customers]);

  const fetchCustomers = async (term) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (term) params.append('search', term);
      params.append('limit', '100');
      const res = await fetch(`${API_URL}/api/financial/customers?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setCustomers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('CustomerPicker fetch error:', err);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (val) => {
    setSearch(val);
    // client-side filter is enough for <=100 customers; also debounce fetch
    if (val.length === 0 || val.length >= 2) {
      const t = setTimeout(() => fetchCustomers(val), 200);
      return () => clearTimeout(t);
    }
  };

  const filtered = search
    ? customers.filter((c) => {
        const q = search.toLowerCase();
        return (
          (c.name || '').toLowerCase().includes(q) ||
          (c.company_name || '').toLowerCase().includes(q) ||
          (c.email || '').toLowerCase().includes(q) ||
          (c.trn || '').toLowerCase().includes(q)
        );
      })
    : customers;

  const pick = (customer) => {
    setSelected(customer);
    setOpen(false);
    setSearch('');
    if (onSelect) onSelect(customer);
  };

  return (
    <div ref={rootRef} className="relative w-full" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 border-2 border-[#00B4D8] rounded-lg bg-white hover:bg-[#f0fbfd] transition-colors text-left"
        data-testid={`${testId}-trigger`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Users className="w-4 h-4 text-[#00B4D8] flex-shrink-0" />
          {selected ? (
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#2B2D42] truncate">
                {selected.company_name || selected.name}
              </div>
              <div className="text-xs text-[#6C757D] truncate">
                {selected.email}
                {selected.trn ? ` • TRN ${selected.trn}` : ''}
              </div>
            </div>
          ) : (
            <span className="text-sm text-[#6C757D]">Select customer from master data</span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-[#6C757D] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden"
          data-testid={`${testId}-dropdown`}
        >
          <div className="p-2 border-b border-gray-100 bg-gray-50">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6C757D]" />
              <input
                type="text"
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search by name, company, email, TRN..."
                autoFocus
                className="w-full pl-8 pr-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
                data-testid={`${testId}-search`}
              />
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-sm text-[#6C757D]">Loading...</div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-center">
                <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <div className="text-sm text-[#6C757D] mb-3">No customers found</div>
                {onCreateNew && (
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      onCreateNew();
                    }}
                    className="inline-flex items-center gap-1 text-sm text-[#00B4D8] hover:underline"
                    data-testid={`${testId}-create-new`}
                  >
                    <Plus className="w-3.5 h-3.5" /> Create new customer
                  </button>
                )}
              </div>
            ) : (
              filtered.map((c) => {
                const isSelected = selected?.id === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => pick(c)}
                    className={`w-full text-left px-3 py-2.5 hover:bg-[#f0fbfd] transition-colors border-b border-gray-50 last:border-b-0 ${
                      isSelected ? 'bg-[#f0fbfd]' : ''
                    }`}
                    data-testid={`${testId}-option-${c.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 text-sm font-semibold text-[#2B2D42]">
                          {c.company_name ? <Building2 className="w-3.5 h-3.5 text-[#FF6B35]" /> : <Users className="w-3.5 h-3.5 text-[#FF6B35]" />}
                          <span className="truncate">{c.company_name || c.name}</span>
                        </div>
                        {c.company_name && c.name && c.company_name !== c.name && (
                          <div className="text-xs text-[#6C757D] pl-5">Contact: {c.name}</div>
                        )}
                        <div className="text-xs text-[#6C757D] pl-5 flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                          <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>
                          {c.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
                          {c.website && (
                            <a
                              href={/^https?:\/\//i.test(c.website) ? c.website : `https://${c.website}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 text-[#023E8A] hover:underline"
                              title="Visit website"
                              data-testid={`${testId}-website-${c.id}`}
                            >
                              <Globe className="w-3 h-3" />{c.website}
                            </a>
                          )}
                        </div>
                        {c.trn && (
                          <div className="text-xs text-[#023E8A] font-medium pl-5 mt-0.5">TRN: {c.trn}</div>
                        )}
                      </div>
                      {isSelected && <Check className="w-4 h-4 text-[#00B4D8] flex-shrink-0 mt-0.5" />}
                    </div>
                  </button>
                );
              })
            )}
          </div>
          {onCreateNew && filtered.length > 0 && (
            <div className="p-2 border-t border-gray-100 bg-gray-50">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onCreateNew();
                }}
                className="w-full inline-flex items-center justify-center gap-1 text-xs text-[#00B4D8] hover:underline py-1"
                data-testid={`${testId}-create-new-footer`}
              >
                <Plus className="w-3 h-3" /> Create new customer
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerPicker;
